import React, { useCallback, useState } from 'react';
import { View, StyleSheet, ScrollView, Text, Platform } from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { ActionButtons } from '@/components';
import { useEditResourceMutation, useDeleteResourceMutation, convertToFormData } from '@/store/api/admin/resourcesApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useEditResourceForm } from '@/components/admin/resources/CreateResource/hooks/useEditResourceForm';
import {
  ResourceDetailsSection,
  ResourceContentSection,
  ResourceFilesSection,
} from '@/components/admin/resources/CreateResource/components';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { ICommunityResource, IFile, IResourceFormValues } from '@/types/admin/resources';
import { normalizeFilesForUpload } from '@/utils/admin/files';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';

interface DesktopEditResourcePanelProps {
  visible: boolean;
  onClose: () => void;
  resource: ICommunityResource | null;
}

export const DesktopEditResourcePanel: React.FC<DesktopEditResourcePanelProps> = ({
  visible,
  onClose,
  resource,
}) => {
  const { redesign } = useAdminColors();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const [editResource, { isLoading: isEditing }] = useEditResourceMutation();
  const [deleteResource, { isLoading: isDeleting }] = useDeleteResourceMutation();
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (!resource) return null;

  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
  } = useEditResourceForm(resource);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    if (!selectedPropertyId) return;
    try {
      const normalizedFiles: IFile[] = await normalizeFilesForUpload(formData.files || []);
      const adjusted: IResourceFormValues = {
        ...formData,
        files: normalizedFiles,
      } as unknown as IResourceFormValues;
      const fd = convertToFormData(adjusted, selectedPropertyId);
      fd.append('id', resource.id.toString());
      const result = await editResource(fd).unwrap();
      if (result?.success !== false) {
        showSuccess('Resource updated successfully!');
        onClose();
      }
    } catch (err: any) {
      showApiError(err, 'Failed to update resource');
    }
  }, [validateForm, selectedPropertyId, formData, editResource, resource?.id, onClose, showSuccess, showApiError]);

  const handleDelete = useCallback(async () => {
    if (Platform.OS === 'web') {
      setShowDeleteModal(true);
      return;
    }

    // Use cross-platform confirmation for native
    const confirmed = await showConfirm(
      'Delete Resource',
      `Are you sure you want to delete "${resource.name}"? This action cannot be undone.`,
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      const result = await deleteResource(resource).unwrap();
      if (result?.success !== false) {
        showSuccess('Resource deleted successfully!');
        onClose();
      }
    } catch (error: any) {
      showApiError(error, 'Failed to delete resource');
    }
  }, [resource, deleteResource, showSuccess, showApiError, onClose]);

  const handleCancel = useCallback(() => {
    setShowDiscardModal(true);
  }, []);

  return (
    <DesktopSidePanel visible={visible} onClose={handleCancel} width={520}>
      <WebConfirmModal
        visible={showDiscardModal}
        title={'Discard Changes'}
        message={'Are you sure you want to discard your changes?'}
        confirmText={'Discard'}
        cancelText={'Keep Editing'}
        onConfirm={() => {
          setShowDiscardModal(false);
          onClose();
        }}
        onCancel={() => setShowDiscardModal(false)}
      />
      <WebConfirmModal
        visible={showDeleteModal}
        title={'Delete Resource'}
        message={`Are you sure you want to delete "${resource.name}"? This action cannot be undone.`}
        confirmText={'Delete'}
        cancelText={'Cancel'}
        onConfirm={async () => {
          try {
            const result = await deleteResource(resource).unwrap();
            if (result?.success !== false) {
              showSuccess('Resource deleted successfully!');
              setShowDeleteModal(false);
              onClose();
            }
          } catch (error: any) {
            showApiError(error, 'Failed to delete resource');
            setShowDeleteModal(false);
          }
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
      <View style={[styles.container, { backgroundColor: redesign.bg.base }]}>
        <DesktopPanelHeader title={resource.name} onClose={handleCancel} />
        <DesktopPanelBody scroll bottomInset={75}>
          <ResourceDetailsSection formData={formData} errors={errors} onFieldChange={handleFieldChange} />
          <ResourceContentSection formData={formData} errors={errors} onFieldChange={handleFieldChange} />
          <ResourceFilesSection formData={formData} errors={errors} onFieldChange={handleFieldChange} />
        </DesktopPanelBody>
        <View
          style={[
            styles.footer,
            { backgroundColor: redesign.bg.base, borderTopColor: redesign.card.border },
          ]}
        >
          <ActionButtons isLoading={isEditing || isDeleting} onCancel={handleCancel} onSubmit={handleSubmit} onDelete={handleDelete} submitText="Update" showDelete />
        </View>
      </View>
    </DesktopSidePanel>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    borderTopWidth: 1,
  },
});


